import { useEffect, useMemo, useRef, useState } from 'react'
import type { TenantSession } from '../lib/session'
import { listHistory, type Redemption } from '../lib/api'
import { screenTitle, buttonRadius } from '../ui'

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
const HISTORY_GRID_COLUMNS = '110px 95px minmax(180px, 1fr) 130px 140px 135px'
const HISTORY_GRID_COLUMNS_NO_MONEY = '110px 95px minmax(180px, 1fr) 130px 135px'
/** `Date#getDay()` is Sun=0..Sat=6; DAY_LABELS leads with Monday, so shift it. */
const dayIndex = (iso: string) => (new Date(iso).getDay() + 6) % 7
type HistoryRange = '7d' | '30d' | '90d' | 'ytd'

function rangeDates(range: HistoryRange): { from: string; to: string } {
  const now = new Date()
  const to = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1))
  const from = range === 'ytd'
    ? new Date(Date.UTC(now.getUTCFullYear(), 0, 1))
    : new Date(to.getTime() - Number(range.slice(0, -1)) * 86400000)
  return { from: from.toISOString().slice(0, 10), to: to.toISOString().slice(0, 10) }
}

/**
 * Counts per local weekday for the WHOLE selected range. The server sends
 * uncapped counts per UTC hour, so each bucket lands on the viewer's weekday
 * exactly. The list below is paged, and busy tenants pass 100 redemptions in
 * a week, so counting the list would show the same newest rows for every
 * range. Falls back to the loaded rows only when the API sends no buckets.
 */
export function usageByDay(rows: Redemption[], hourly?: { hour: string; count: number }[]): number[] {
  const counts = [0, 0, 0, 0, 0, 0, 0]
  if (hourly) {
    for (const b of hourly) counts[dayIndex(`${b.hour}:00:00.000Z`)] += b.count
    return counts
  }
  for (const r of rows) counts[dayIndex(r.redeemedAt)]++
  return counts
}

const RANGE_LABELS: Record<HistoryRange, string> = { '7d': 'last 7 days', '30d': 'last 30 days', '90d': 'last 90 days', ytd: 'this year' }

export function History({ session, hideMonetaryValues = false }: { session: TenantSession; hideMonetaryValues?: boolean }) {
  const [rows, setRows] = useState<Redemption[]>([])
  const [total, setTotal] = useState<number | null>(null)
  const [hourly, setHourly] = useState<{ hour: string; count: number }[] | undefined>()
  const [range, setRange] = useState<HistoryRange>('30d')
  // The range the rows/chart/total in state were actually fetched for. `range`
  // is only what the buttons say; after a failed fetch the two differ, and every
  // label, the chart and "Show more" must follow what is on screen.
  const [loadedRange, setLoadedRange] = useState<HistoryRange | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [err, setErr] = useState('')
  const [moreErr, setMoreErr] = useState('')
  // Only the newest load may write state, so a slow old range cannot land
  // after a newer one and relabel its rows.
  const loadSeq = useRef(0)

  const load = async () => {
    const seq = ++loadSeq.current
    const requested = range
    setLoading(true); setErr(''); setMoreErr('')
    try {
      const page = await listHistory(session.token, undefined, rangeDates(requested))
      if (seq !== loadSeq.current) return
      setRows(page.redemptions)
      setTotal(page.total ?? null)
      setHourly(page.hourly)
      setLoadedRange(requested)
    }
    catch (e) {
      if (seq !== loadSeq.current) return
      setErr(e instanceof Error ? e.message : 'load failed'); console.error('[tenant] history', e)
    }
    finally { if (seq === loadSeq.current) setLoading(false) }
  }
  const loadMore = async () => {
    const last = rows[rows.length - 1]
    // Page within the range the rows belong to, never the one just clicked.
    if (!last || !loadedRange) return
    const seq = loadSeq.current
    setLoadingMore(true); setMoreErr('')
    try {
      const page = await listHistory(session.token, undefined, rangeDates(loadedRange), last.redeemedAt)
      if (seq !== loadSeq.current) return
      setRows(prev => [...prev, ...page.redemptions])
    }
    catch (e) {
      if (seq !== loadSeq.current) return
      setMoreErr(e instanceof Error ? e.message : 'load failed'); console.error('[tenant] history more', e)
    }
    finally { setLoadingMore(false) }
  }
  useEffect(() => { void load() }, [range]) // eslint-disable-line react-hooks/exhaustive-deps

  const fmtTime = (iso: string) => {
    try { return new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) }
    catch { return iso }
  }

  const counts = useMemo(() => usageByDay(rows, hourly), [rows, hourly])
  const shownRange = loadedRange ?? range
  const rangeTotal = total ?? rows.length
  // Rows kept from a different range than the one selected (the refresh failed).
  const stale = loadedRange !== null && loadedRange !== range
  const maxCount = Math.max(...counts)
  const gridColumns = hideMonetaryValues ? HISTORY_GRID_COLUMNS_NO_MONEY : HISTORY_GRID_COLUMNS

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
        <h2 style={screenTitle}>Redemption history</h2>
        <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-gray-600)' }}>{loading ? 'loading…' : loadedRange === null ? '' : total === null ? `${rows.length} most recent` : `${total} in the ${RANGE_LABELS[shownRange]}`}</span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 4, background: 'var(--color-gray-100)', borderRadius: 8, padding: 3 }}>
          {([['7d', '7 days'], ['30d', '30 days'], ['90d', '90 days'], ['ytd', 'YTD']] as const).map(([value, label]) => (
            <button key={value} onClick={() => setRange(value)} style={{ border: 0, borderRadius: 6, padding: '6px 9px', background: range === value ? '#fff' : 'transparent', boxShadow: range === value ? '0 1px 3px rgba(0,0,0,0.12)' : 'none', fontFamily: 'inherit', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>{label}</button>
          ))}
        </div>
      </div>

      {err && rows.length > 0 && (
        <div role="alert" style={{ marginBottom: 14 }}>
          <LoadError
            message={stale ? `${err}. Still showing the ${RANGE_LABELS[shownRange]}, not the ${RANGE_LABELS[range]} you selected.` : err}
            onRetry={() => void load()}
          />
        </div>
      )}

      {maxCount > 0 && (
        <div style={{ background: '#fff', border: '1px solid var(--color-gray-200)', borderRadius: 12, padding: '16px 18px', marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 12 }}>
            <div style={{ fontSize: 14, fontWeight: 700 }}>Usage by day</div>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-gray-600)' }}>
              Peak {maxCount} on {DAY_LABELS[counts.indexOf(maxCount)]} · of {rangeTotal} redemptions in the {RANGE_LABELS[shownRange]}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10, height: 90 }}>
            {counts.map((c, i) => (
              <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, height: '100%', justifyContent: 'flex-end' }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--color-gray-600)' }}>{c || ''}</div>
                <div style={{
                  width: '100%', maxWidth: 36, borderRadius: 4,
                  height: `${Math.max(4, (c / maxCount) * 64)}px`,
                  background: c === maxCount ? '#7F3F98' : 'var(--color-gray-300)',
                }} />
                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--color-gray-600)' }}>{DAY_LABELS[i]}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={{ background: '#fff', borderRadius: 12, overflowX: 'auto', border: '1px solid var(--color-gray-200)' }}>
        <div style={{ display: 'grid', width: '100%', gridTemplateColumns: gridColumns, padding: '10px 16px', borderBottom: '1px solid var(--color-gray-200)', fontSize: 11, fontWeight: 800, letterSpacing: '0.5px', textTransform: 'uppercase', color: 'var(--color-gray-600)' }}>
          <div>Code</div><div>Plate</div><div>Lot</div><div>Tablet</div>{!hideMonetaryValues && <div>Discount</div>}<div>When</div>
        </div>
        {rows.length === 0 ? (
          err ? (
            <div role="alert" style={{ padding: 16 }}><LoadError message={err} onRetry={() => void load()} /></div>
          ) : (
            <div style={{ padding: 32, textAlign: 'center', fontSize: 13, fontWeight: 600, color: 'var(--color-gray-600)' }}>
              {loading ? '' : 'No redemptions yet. Once visitors use your validations, they show up here.'}
            </div>
          )
        ) : rows.map((r, i) => (
          <div key={i} style={{ display: 'grid', width: '100%', gridTemplateColumns: gridColumns, alignItems: 'center', padding: '11px 16px', borderBottom: '1px solid var(--color-gray-100)', fontSize: 13 }}>
            <code style={{ fontFamily: 'monospace', fontSize: 12 }}>{r.code}</code>
            <div style={{ fontFamily: 'monospace', fontSize: 12 }}>{r.plate}</div>
            <div style={{ color: 'var(--color-gray-700)' }}>{r.zoneName}</div>
            {/* Which front-desk tablet issued this (migration 0031) — the
                column that makes the Tablets tab worth having, because two
                counters are otherwise indistinguishable here.

                A null tablet is NOT a missing value: it means the driver
                applied the code themselves at checkout (a coupon or a QR),
                which is the majority of rows on most offers. Rendering that as
                a blank would read as a data defect on the exact screen someone
                reconciles an invoice against, so it gets its own words.
                `undefined` is the third case — an API too old to send the field
                at all — where we genuinely don't know, and an em dash is the
                honest answer. */}
            <div style={{ fontSize: 12, color: 'var(--color-gray-700)', paddingRight: 8 }}>
              {r.deviceName
                ? r.deviceName
                : r.deviceName === null
                  ? <span style={{ color: 'var(--color-gray-600)' }}>Self-applied</span>
                  /* The bare em-dash KEEPS gray-400 (1.69:1) on purpose — it is an
                     absence marker, not text, and de-emphasising "there is nothing
                     here" is the point. "Self-applied" above it moved to gray-600
                     because that one is a real value a reader has to take in. */
                  : <span style={{ color: 'var(--color-gray-400)' }}>—</span>}
            </div>
            {/* The TOTAL the validation gave away, not just the checkout
                discount — a visitor who added time drew on the same grant, and
                showing only the original figure under-reports what this tenant
                actually comped. The extension part is broken out rather than
                folded in silently, because "$5.99 + $3.00 added time" is the
                line someone reconciles an invoice against. Falls back to
                discountCents so a portal deployed ahead of the API still
                renders the number it always did. */}
            {!hideMonetaryValues && (
              <div style={{ fontFamily: 'monospace', fontSize: 12 }}>
                ${((r.totalDiscountCents ?? r.discountCents) / 100).toFixed(2)}
                {(r.extensionDiscountCents ?? 0) > 0 && (
                  <span style={{ display: 'block', fontSize: 10, fontWeight: 600, color: 'var(--color-gray-600)' }}>
                    incl. ${((r.extensionDiscountCents ?? 0) / 100).toFixed(2)} added time
                  </span>
                )}
              </div>
            )}
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-gray-600)' }}>{fmtTime(r.redeemedAt)}</div>
          </div>
        ))}
      </div>
      {total !== null && rows.length > 0 && rows.length < total && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, marginTop: 12 }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-gray-600)' }}>Showing {rows.length} of {total}</span>
          <button onClick={() => void loadMore()} disabled={loadingMore || loading} style={{ padding: '8px 18px', borderRadius: buttonRadius, background: '#fff', color: '#7F3F98', border: '1.5px solid #7F3F98', fontWeight: 700, fontSize: 12, cursor: loadingMore ? 'default' : 'pointer', fontFamily: 'inherit' }}>
            {loadingMore ? 'Loading…' : 'Show more'}
          </button>
        </div>
      )}
      {moreErr && (
        <div role="alert" style={{ marginTop: 12 }}>
          <LoadError message={`Couldn't load more redemptions. ${moreErr}`} onRetry={() => void loadMore()} />
        </div>
      )}
    </div>
  )
}

// Inline load-failure block — shown in place of the empty state when the fetch
// fails, so a partner never reads "couldn't load" as "no redemptions". Mirrors
// the red banner tone used in Validate / SignIn; Retry re-runs the loader.
function LoadError({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div style={{ background: 'rgba(237,28,36,0.10)', border: '1.5px solid rgba(237,28,36,0.4)', borderRadius: 12, padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
      <div style={{ flex: 1, minWidth: 220 }}>
        <div style={{ fontSize: 14, fontWeight: 800, color: '#b91c1c', marginBottom: 2 }}>Couldn't load</div>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-gray-700)', lineHeight: 1.5 }}>{message}</div>
      </div>
      <button onClick={onRetry} style={{ padding: '8px 18px', borderRadius: buttonRadius, background: '#7F3F98', color: '#fff', border: 'none', fontWeight: 700, fontSize: 12, cursor: 'pointer', fontFamily: 'inherit' }}>Retry</button>
    </div>
  )
}
